import React, { useContext, useEffect, useState, useRef } from 'react';
import { IEditorProps } from '../Editor';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import './LegacyEditor.less';
import { SkeletonLoader } from 'loaders/SkeletonLoader';
import { Label } from 'components/inputs/Label';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service/MappingContext';
import { TabbingContext } from 'contexts/TabbingContext';
import { EditorLabel } from '../EditorLabel';
import { EditorValidationWrapper } from '../EditorValidationWrapper';

export type RenderLegacyEditor = (options: LegacyEditorOptions) => void;
export interface ILegacyEditorRenderProps {
  targetRef: React.MutableRefObject<HTMLElement>;
  instanceId?: string;
  wrapperStyle: React.CSSProperties;
}

export interface ILegacyEditorProps extends IEditorProps {
  /** Pass specific content to be rendered into */
  content?: (props: ILegacyEditorRenderProps) => React.ReactNode;
  idPrefix?: string;
  /** Custom control options for cases where some things are defined or rendered on the React side */
  options?: any;
  /** To specify a min height based on some configuration for a particular editor */
  minHeight?: number;
}

const EXPECTED_LABEL_HEIGHT = 27;

/**
 * Legacy Editor wrapper component. Loads legacy editor HTML restfully, and renders it into a container element.
 *
 * It also renders expected editor height to minimize impact on surrounding editors.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */

export function LegacyEditor({
  child: node,
  path,
  content,
  idPrefix,
  options,
  minHeight,
  hideInputLabel,
}: ILegacyEditorProps) {
  const context = useContext(PropertyEditorContext);
  const { editingContextId, renderLegacyEditor } = context;
  const { tabIndex } = useContext(TabbingContext);
  const pathString = path.join(',');
  const editorValueString = JSON.stringify(node.Value.OutputValue); // TODO will byte arrays explode this?
  const instanceId = useRef(idPrefix || 'legacy-editor-target-' + Math.round(Math.random() * 1000000));
  const onCleanup = useRef<Function>();
  const [isLoading, setIsLoading] = useState(true);
  const targetRef = useRef<HTMLDivElement>(document.createElement('div'));
  const height = minHeight || LegacyEditorHeights[node.Node.MvcEditorName as keyof typeof LegacyEditorHeights];
  const renderDirectly = ClientSideLegacyEditors.includes(node.Node.MvcEditorName) && !!renderLegacyEditor;

  const getCleanup = (cleanup: () => void) => {
    onCleanup.current = cleanup;
  };

  /**
   * A callback for when JS is loaded on MVC side, for ones like ckeditor that take a while to download are loaded,
   * so we can keep the skeleton loader up until it's actually ready
   */
  const afterInit = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (DEV_SERVER || IS_TEST) {
      // skip making this trip in dev server for now.
      return;
    }
    if (renderDirectly && renderLegacyEditor && targetRef.current) {
      renderLegacyEditor({
        node,
        path,
        target: targetRef.current,
        instanceId: instanceId.current,
        tabIndex,
        editingContextId: context.editingContextId,
        mappingContext: context.mappingContext,
        options,
        getCleanup,
        afterInit,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContextId, pathString, editorValueString, targetRef.current]);

  useEffect(() => {
    // just cleanup if need be
    return function cleanup() {
      onCleanup.current && onCleanup.current();
    };
  }, []);

  const wrapperStyle = { minHeight: !isLoading ? `${height + EXPECTED_LABEL_HEIGHT}px` : '' };

  return (
    <div className="dpc-legacy-editor" title={node.Node.DisplayLabel}>
      {isLoading && (
        <Label label={node.Node.DisplayLabel}>
          <SkeletonLoader height={height} />
        </Label>
      )}
      {renderDirectly ? (
        content ? (
          /* if we have react children for particular legacy editor concerns, render those */
          content({ targetRef, instanceId: instanceId.current, wrapperStyle })
        ) : (
          /* otherwise use the direct render default */
          <EditorLabel editor={node} path={path} hideInputLabel={hideInputLabel}>
            <EditorValidationWrapper editor={node}>
              <div ref={targetRef} id={instanceId.current} />
            </EditorValidationWrapper>
          </EditorLabel>
        )
      ) : (
        /* Target Element for MVC call result: */
        <div ref={targetRef} id={instanceId.current} />
      )}
    </div>
  );
}

/**
 * These are rendered type-script to type-script, no MVC API calls.
 */
export const ClientSideLegacyEditors = [
  'TypePickerEditor',
  'ChartPartPicker',
  'ReportFieldSelectorEditor',
  'TextMergeFieldSelectorEditorView',
  'RichTextInputEditor',
];

/**
 * An object with MvcEditorName keys, with values being the expected height
 * of the user. Having this height is a temporary workaround for the poor
 * user experience of these flattening and making the UI wiggle dramatically
 * during the rendering & process of these editors.
 */
export const LegacyEditorHeights = {
  // Rendering via MVC API calls
  //-- rendering directly TS to TS
  RichTextInputEditor: 189,
  TypePickerEditor: 30, // needs tree-view
  ReportFieldSelectorEditor: 30, // needs tree-view
  TextMergeFieldSelectorEditorView: 80, // needs tree-view
  //-- converted to React:
  // ChartPartPicker: 30, // just a combo with some data
  // StringEditor: 30, // was just a debounced string field, which we have
  // ChartSeriesEditor: 30,
  // DateEditor: 30,
  // DateAndTimeEditor: 30,
  // TimePickerEditor: 30,
  // CalendarEditor: 310,
};

export const LegacyEditorList = Array.from(Object.keys(LegacyEditorHeights));

export interface LegacyEditorOptions {
  node: ContextProviderNode;
  path: string[];
  target: HTMLElement;
  instanceId: string;
  tabIndex: number;
  editingContextId: string;
  mappingContext: MappingContext;
  options?: any;
  afterInit: () => void;
  getCleanup: (onCleanup: () => void) => void;
}
